import { useMemo, useState } from 'react'
import { Alert, Button, Input, Select, Space, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { EyeOutlined, PlusOutlined } from '@ant-design/icons'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { matchesSearch } from '@/shared/lib/localList'
import { formatMoneyValue } from '@/shared/lib/money'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { ShareAmount, SignedAmount } from '@/features/stakeholder/ShareAmount'
import { StakeholderFormModal } from '@/features/stakeholder/StakeholderFormModal'
import {
  bpToPercent,
  fetchStakeholders,
  type StakeholderRow,
  type StatusFilter,
} from '@/features/stakeholder/stakeholderApi'

export function StakeholdersPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [status, setStatus] = useState<StatusFilter>('ACTIVE')
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)

  const list = useQuery({
    queryKey: queryKeys.stakeholder.list(status),
    queryFn: ({ signal }) => fetchStakeholders(status, signal),
  })

  const rows = useMemo(
    () => (list.data ?? []).filter((row) => matchesSearch(search, row.code, row.name, row.scope?.name ?? '')),
    [list.data, search],
  )

  const columns: ColumnsType<StakeholderRow> = [
    { key: 'code', title: t('stakeholder.field.code'), width: 120, fixed: 'left', render: (_, row) => row.code },
    {
      key: 'name',
      title: t('stakeholder.field.name'),
      width: 220,
      render: (_, row) => <Link to={`/finance/stakeholders/${row.id}`}>{row.name}</Link>,
    },
    { key: 'type', title: t('stakeholder.field.type'), width: 140, render: (_, row) => t(`stakeholder.type.${row.stakeholderType}`) },
    {
      key: 'scope',
      title: t('stakeholder.field.scope'),
      width: 180,
      render: (_, row) => (row.scope ? row.scope.name : t('stakeholder.scope.company')),
    },
    {
      key: 'ownership',
      title: t('stakeholder.summary.ownership'),
      width: 110,
      align: 'right',
      render: (_, row) => t('stakeholder.percent', { value: bpToPercent(row.summary.ownershipBp) }),
    },
    {
      key: 'investment',
      title: t('stakeholder.summary.totalInvestment'),
      width: 150,
      align: 'right',
      render: (_, row) => formatMoneyValue(row.summary.totalInvestment),
    },
    {
      key: 'profitShare',
      title: t('stakeholder.summary.profitShare'),
      width: 150,
      align: 'right',
      render: (_, row) => formatMoneyValue(row.summary.profitShare),
    },
    {
      key: 'lossShare',
      title: t('stakeholder.summary.lossShare'),
      width: 150,
      align: 'right',
      render: (_, row) => <ShareAmount value={row.summary.lossShare} kind="LOSS" />,
    },
    {
      key: 'profitPaid',
      title: t('stakeholder.summary.profitPaid'),
      width: 150,
      align: 'right',
      render: (_, row) => formatMoneyValue(row.summary.profitPaid),
    },
    {
      key: 'withdrawals',
      title: t('stakeholder.summary.withdrawals'),
      width: 150,
      align: 'right',
      render: (_, row) => formatMoneyValue(row.summary.withdrawals),
    },
    {
      key: 'balance',
      title: t('stakeholder.summary.currentBalance'),
      width: 160,
      align: 'right',
      render: (_, row) => <SignedAmount value={row.summary.currentBalance} />,
    },
    {
      key: 'active',
      title: t('stakeholder.field.status'),
      width: 110,
      render: (_, row) =>
        row.active ? <Tag color="green">{t('stakeholder.state.active')}</Tag> : <Tag>{t('stakeholder.state.inactive')}</Tag>,
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'open',
              label: t('stakeholder.action.open'),
              icon: <EyeOutlined />,
              onClick: () => navigate(`/finance/stakeholders/${row.id}`),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.stakeholders')}
        description={t('pageIntro.stakeholders')}
        extra={
          <Can permission="stakeholder.manage">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
              {t('stakeholder.action.create')}
            </Button>
          </Can>
        }
      />
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          style={{ minWidth: 260 }}
          placeholder={t('stakeholder.search')}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Select<StatusFilter>
          style={{ minWidth: 160 }}
          value={status}
          onChange={setStatus}
          options={[
            { value: 'ACTIVE', label: t('stakeholder.state.active') },
            { value: 'INACTIVE', label: t('stakeholder.state.inactive') },
            { value: 'ALL', label: t('stakeholder.state.all') },
          ]}
        />
      </Space>
      {list.isError ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(list.error, t)} /> : null}
      <Table<StakeholderRow>
        size="small"
        rowKey="id"
        loading={list.isPending}
        pagination={false}
        dataSource={rows}
        columns={columns}
        scroll={{ x: 1950 }}
        locale={{ emptyText: search ? t('stakeholder.emptyFiltered') : t('stakeholder.empty') }}
        onRow={(row) => ({ onDoubleClick: () => navigate(`/finance/stakeholders/${row.id}`) })}
      />
      <StakeholderFormModal
        open={creating}
        stakeholder={null}
        onClose={() => setCreating(false)}
        onSaved={(saved) => navigate(`/finance/stakeholders/${saved.id}`)}
      />
    </>
  )
}
